import { MaybeUndef, Undef } from 'types/UtilityTypes';

import { LTLItem, LTLUnitSystem, LTL_UNIT_SYSTEMS } from '../types';
import {
  type HeightConfig,
  type ShipmentLayoutMetrics,
  buildShipmentLayoutMetrics,
  calculateLTLMetrics,
  normalizeHandlingUnitsForPacking,
  resolveMaxStackHeightInches,
} from './ltlMetrics';

// Minimum ft^3 reduction vs baseline required to surface a cubic-feet-only side-load
// suggestion (when linear-feet gain is below SIDELOAD_MIN_LINEAR_FEET_IMPROVEMENT).
// Use a conservative threshold so minor volume noise does not trigger suggestions.
export const SIDELOAD_MIN_CUBIC_FEET_IMPROVEMENT = 5;

type TaiLTLMetrics = ReturnType<typeof calculateLTLMetrics>;

const EPSILON = 0.0001;
const SIDELOAD_MAX_PASSES = 2;
export const SIDELOAD_MAX_SPLIT_DEPTH = 2;
// Shelf-depth slack uses max(ratio x baseline, floor inches).
const SIDELOAD_SHELF_DEPTH_BASELINE_RATIO = 0.24;
const SIDELOAD_SHELF_DEPTH_MIN_SLACK_INCHES = 12;
export const SIDELOAD_MIN_LINEAR_FEET_IMPROVEMENT = 1;

// Allowed extra inches on max shelf depth vs baseline before a sideload candidate is rejected.
// max(24% of baseline depth, 12"); when baseline depth is ~0, use 12" minimum slack.
export function sideLoadMaxShelfDepthIncreaseInches(
  baselineMaxShelfDepthInches: number
): number {
  if (baselineMaxShelfDepthInches <= EPSILON) {
    return SIDELOAD_SHELF_DEPTH_MIN_SLACK_INCHES;
  }
  return Math.max(
    baselineMaxShelfDepthInches * SIDELOAD_SHELF_DEPTH_BASELINE_RATIO,
    SIDELOAD_SHELF_DEPTH_MIN_SLACK_INCHES
  );
}

export interface SideLoadSuggestionItem {
  itemIndex: number;
  itemId: string;
  rotatedUnits: number;
}

type SideLoadMetricSlice = Pick<
  TaiLTLMetrics,
  'linearFeet' | 'totalCubicFeet' | 'pcf'
> & {
  shipmentLengthInches: number;
  maxShelfDepthInches: number;
};

export interface SideLoadOpportunityResult {
  // True when linear feet improve by at least SIDELOAD_MIN_LINEAR_FEET_IMPROVEMENT.
  hasOpportunity: boolean;
  // True when LF does not meet that bar but a rotation still reduces total cubic
  // feet meaningfully; user may choose whether to apply.
  hasCubicFeetOnlyOpportunity: boolean;
  current: SideLoadMetricSlice;
  optimized: SideLoadMetricSlice;
  suggestions: SideLoadSuggestionItem[];
}

interface SideLoadOptions {
  allowSplitMerge?: boolean;
  isItemStackable?: (item: LTLItem) => boolean;
  maxStackHeightInches?: number;
}

export function hasExplicitPositiveHandlingUnits(
  handlingUnits: LTLItem['handlingUnits']
): boolean {
  const parsedHandlingUnits =
    typeof handlingUnits === 'number' ? handlingUnits : Number(handlingUnits);
  return Number.isFinite(parsedHandlingUnits) && parsedHandlingUnits > 0;
}

export function allocateDerivedItemId(): string {
  const cryptoApi = globalThis.crypto as Undef<Crypto>;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  return `sideload-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function makeRotationStateKey(rotatedById: Map<string, number>): string {
  return JSON.stringify(
    [...rotatedById.entries()].sort((leftEntry, rightEntry) =>
      leftEntry[0].localeCompare(rightEntry[0])
    )
  );
}

// Split line-total weight by HU counts for a partial side-load. Values are
// rounded to 5 decimal places; the second share is the remainder so the pair
// sums to the original weight (within float tolerance).
export function splitSideLoadLineWeightByHuShares(
  baseWeight: MaybeUndef<number>,
  normalHandlingUnits: number,
  rotatedHandlingUnits: number
): { normal: Undef<number>; rotated: Undef<number> } {
  if (baseWeight == null) {
    return {
      normal: undefined,
      rotated: undefined,
    };
  }
  if (!Number.isFinite(baseWeight)) {
    return {
      normal: undefined,
      rotated: undefined,
    };
  }
  const totalHandlingUnits = normalHandlingUnits + rotatedHandlingUnits;
  if (
    totalHandlingUnits <= 0 ||
    normalHandlingUnits < 0 ||
    rotatedHandlingUnits < 0
  ) {
    return { normal: baseWeight, rotated: 0 };
  }

  const round5 = (n: number) => Math.round(n * 100_000) / 100_000;
  const normal = round5(
    baseWeight * (normalHandlingUnits / totalHandlingUnits)
  );
  const rotated = round5(baseWeight - normal);
  return { normal, rotated };
}

// Split integer piece count by HU shares. Parent/normal row gets the remainder
// so totals stay exact and conflict-prone odd splits prefer the parent row.
export function splitSideLoadLinePiecesByHuShares(
  basePieces: MaybeUndef<number>,
  normalHandlingUnits: number,
  rotatedHandlingUnits: number
): { normal: Undef<number>; rotated: Undef<number> } {
  if (basePieces == null) {
    return {
      normal: undefined,
      rotated: undefined,
    };
  }
  if (!Number.isFinite(basePieces)) {
    return {
      normal: undefined,
      rotated: undefined,
    };
  }

  const totalPieces = Math.max(0, Math.floor(basePieces));
  const totalHandlingUnits = normalHandlingUnits + rotatedHandlingUnits;
  if (
    totalHandlingUnits <= 0 ||
    normalHandlingUnits < 0 ||
    rotatedHandlingUnits < 0
  ) {
    return { normal: totalPieces, rotated: 0 };
  }

  let rotated = Math.floor(
    (totalPieces * rotatedHandlingUnits) / totalHandlingUnits
  );
  let normal = totalPieces - rotated;

  // Enforce minimum of 1 piece on each split row when both rows exist.
  if (totalPieces > 0 && normalHandlingUnits > 0 && rotatedHandlingUnits > 0) {
    if (normal === 0) {
      normal = 1;
    }
    if (rotated === 0) {
      rotated = 1;
    }
  }

  return { normal, rotated };
}

// Derived list for sideload trials; partial splits split line-total weight and
// piece count by HU share only.
export function buildDerivedItemsForSideLoad(
  items: LTLItem[],
  rotatedByItemId: Map<string, number>,
  options?: SideLoadOptions
): LTLItem[] {
  const allowSplitMerge = options?.allowSplitMerge ?? true;
  const derived: LTLItem[] = [];

  for (const item of items) {
    const handlingUnitCount = normalizeHandlingUnitsForPacking(
      item.handlingUnits
    );
    const rotatedUnitsRaw = rotatedByItemId.get(item.itemId) ?? 0;
    const rotatedUnits = Math.max(
      0,
      Math.min(handlingUnitCount, Math.floor(rotatedUnitsRaw))
    );
    const splitDepth = item.splitDepth ?? 0;
    const canSplitThisRow =
      allowSplitMerge && splitDepth < SIDELOAD_MAX_SPLIT_DEPTH;
    const effectiveRotatedUnits =
      canSplitThisRow ||
      rotatedUnits === 0 ||
      rotatedUnits === handlingUnitCount
        ? rotatedUnits
        : 0;

    if (handlingUnitCount <= 0 || effectiveRotatedUnits === 0) {
      derived.push({ ...item });
      continue;
    }

    if (effectiveRotatedUnits === handlingUnitCount) {
      derived.push({
        ...item,
        parentItemId: item.parentItemId ?? item.itemId,
        length: item.width,
        width: item.length,
      });
      continue;
    }

    const normalHandlingUnits = handlingUnitCount - effectiveRotatedUnits;
    const rotatedHandlingUnits = effectiveRotatedUnits;
    const { normal: normalWeight, rotated: rotatedWeight } =
      splitSideLoadLineWeightByHuShares(
        item.weight,
        normalHandlingUnits,
        rotatedHandlingUnits
      );
    const { normal: normalPieces, rotated: rotatedPieces } =
      splitSideLoadLinePiecesByHuShares(
        item.pieces,
        normalHandlingUnits,
        rotatedHandlingUnits
      );

    derived.push({
      ...item,
      handlingUnits: normalHandlingUnits,
      weight: normalWeight,
      pieces: normalPieces,
      splitDepth: splitDepth + 1,
    });
    derived.push({
      ...item,
      itemId: allocateDerivedItemId(),
      parentItemId: item.itemId,
      length: item.width,
      width: item.length,
      handlingUnits: rotatedHandlingUnits,
      weight: rotatedWeight,
      pieces: rotatedPieces,
      splitDepth: splitDepth + 1,
    });
  }

  return derived;
}

function metricSliceFromEval(
  layout: ShipmentLayoutMetrics,
  metrics: TaiLTLMetrics
): SideLoadMetricSlice {
  return {
    linearFeet: metrics.linearFeet,
    totalCubicFeet: metrics.totalCubicFeet,
    pcf: metrics.pcf,
    shipmentLengthInches: layout.totalLengthInches,
    maxShelfDepthInches: layout.maxShelfDepthInches,
  };
}

function scoreIsBetter(
  candidateMetrics: SideLoadMetricSlice,
  currentBestMetrics: SideLoadMetricSlice
): boolean {
  if (candidateMetrics.linearFeet !== currentBestMetrics.linearFeet) {
    return candidateMetrics.linearFeet < currentBestMetrics.linearFeet;
  }
  if (
    Math.abs(
      candidateMetrics.totalCubicFeet - currentBestMetrics.totalCubicFeet
    ) > EPSILON
  ) {
    return candidateMetrics.totalCubicFeet < currentBestMetrics.totalCubicFeet;
  }
  if (
    Math.abs(
      candidateMetrics.shipmentLengthInches -
        currentBestMetrics.shipmentLengthInches
    ) > EPSILON
  ) {
    return (
      candidateMetrics.shipmentLengthInches <
      currentBestMetrics.shipmentLengthInches
    );
  }
  if (
    Math.abs(
      candidateMetrics.maxShelfDepthInches -
        currentBestMetrics.maxShelfDepthInches
    ) > EPSILON
  ) {
    return (
      candidateMetrics.maxShelfDepthInches <
      currentBestMetrics.maxShelfDepthInches
    );
  }
  return candidateMetrics.pcf > currentBestMetrics.pcf + EPSILON;
}

// Looks up layout + metrics for a rotation map. The cache is only for the
// duration of one calculateBestSideLoadOpportunity call (a fresh Map each time).
// There is no TTL: it avoids recomputing identical rotation keys in the same run.
function evaluateRotatedConfiguration(
  items: LTLItem[],
  rotatedById: Map<string, number>,
  units: LTLUnitSystem,
  customConfig: Undef<HeightConfig>,
  stackingEnabled: boolean,
  options: SideLoadOptions,
  cache: Map<string, { layout: ShipmentLayoutMetrics; metrics: TaiLTLMetrics }>
): { layout: ShipmentLayoutMetrics; metrics: TaiLTLMetrics } {
  const key = makeRotationStateKey(rotatedById);
  const hit = cache.get(key);
  if (hit) {
    return hit;
  }

  const derivedItems = buildDerivedItemsForSideLoad(
    items,
    rotatedById,
    options
  );
  const maxStackHeightInches =
    options.maxStackHeightInches ?? resolveMaxStackHeightInches(customConfig);
  const layout = buildShipmentLayoutMetrics(derivedItems, units, {
    isItemStackable: options.isItemStackable,
    stackingEnabled,
    maxStackHeightInches,
    customConfig,
  });
  const metrics = calculateLTLMetrics(derivedItems, units, {
    customConfig,
    isItemStackable: options.isItemStackable,
    useLayoutBoundingBoxForCubicFeet: true,
    stackingEnabled,
    maxStackHeightInches,
    precomputedLayout: layout,
  });
  const payload = { layout, metrics };
  cache.set(key, payload);
  return payload;
}

export function calculateBestSideLoadOpportunity(
  items: LTLItem[],
  units: LTLUnitSystem = LTL_UNIT_SYSTEMS.IMPERIAL,
  customConfig?: HeightConfig,
  stackingEnabled = false,
  options?: SideLoadOptions
): SideLoadOpportunityResult {
  const sideLoadOptions: SideLoadOptions = {
    allowSplitMerge: options?.allowSplitMerge ?? true,
    isItemStackable: options?.isItemStackable,
    maxStackHeightInches: options?.maxStackHeightInches,
  };
  const evalCache: Map<
    string,
    { layout: ShipmentLayoutMetrics; metrics: TaiLTLMetrics }
  > = new Map();

  const baselineEval = evaluateRotatedConfiguration(
    items,
    new Map(),
    units,
    customConfig,
    stackingEnabled,
    sideLoadOptions,
    evalCache
  );

  const baselineMetrics = baselineEval.metrics;
  const baselineLayout = baselineEval.layout;
  const baselineMaxShelfDepth = baselineLayout.maxShelfDepthInches;
  const baselineShelfDepthSlack = sideLoadMaxShelfDepthIncreaseInches(
    baselineMaxShelfDepth
  );

  const baseline = metricSliceFromEval(baselineLayout, baselineMetrics);

  const eligible = items
    .map((item, itemIndex) => ({ item, itemIndex }))
    .filter(
      ({ item }) =>
        item.canSideLoad === true &&
        (item.length ?? 0) > 0 &&
        (item.width ?? 0) > 0 &&
        (item.height ?? 0) > 0 &&
        (item.length ?? 0) !== (item.width ?? 0) &&
        hasExplicitPositiveHandlingUnits(item.handlingUnits)
    );

  if (eligible.length === 0) {
    return {
      hasOpportunity: false,
      hasCubicFeetOnlyOpportunity: false,
      current: baseline,
      optimized: baseline,
      suggestions: [],
    };
  }

  const rotatedById = new Map<string, number>();
  for (const { item } of eligible) {
    rotatedById.set(item.itemId, 0);
  }

  let bestOptimized = baseline;
  let bestSuggestions: SideLoadSuggestionItem[] = [];
  const toSuggestions = (map: Map<string, number>): SideLoadSuggestionItem[] =>
    eligible
      .map(({ item: it, itemIndex }) => ({
        itemIndex,
        itemId: it.itemId,
        rotatedUnits: map.get(it.itemId) ?? 0,
      }))
      .filter((s) => s.rotatedUnits > 0);
  const toRotationCandidates = (item: LTLItem): number[] => {
    const handlingUnitCount = normalizeHandlingUnitsForPacking(
      item.handlingUnits
    );
    const splitDepth = item.splitDepth ?? 0;
    const canSplitThisRow =
      sideLoadOptions.allowSplitMerge === true &&
      splitDepth < SIDELOAD_MAX_SPLIT_DEPTH;
    const baseCandidates = canSplitThisRow
      ? [
          0,
          1,
          handlingUnitCount - 1,
          Math.floor(handlingUnitCount / 2),
          handlingUnitCount,
        ]
      : [0, handlingUnitCount];
    return Array.from(
      new Set(
        baseCandidates.filter(
          (rotatedUnits) =>
            rotatedUnits >= 0 && rotatedUnits <= handlingUnitCount
        )
      )
    ).sort(
      (leftCandidateRotatedUnits, rightCandidateRotatedUnits) =>
        leftCandidateRotatedUnits - rightCandidateRotatedUnits
    );
  };

  for (let pass = 0; pass < SIDELOAD_MAX_PASSES; pass += 1) {
    let passBestOptimized: SideLoadMetricSlice | null = null;
    let passBestRotatedById: Map<string, number> | null = null;

    if (eligible.length === 1) {
      const singleEligibleItem = eligible[0].item;
      const candidates = toRotationCandidates(singleEligibleItem);

      for (const candidateRotatedUnits of candidates) {
        const rotatedNext = new Map(rotatedById);
        rotatedNext.set(singleEligibleItem.itemId, candidateRotatedUnits);

        const { layout, metrics } = evaluateRotatedConfiguration(
          items,
          rotatedNext,
          units,
          customConfig,
          stackingEnabled,
          sideLoadOptions,
          evalCache
        );
        const optimized = metricSliceFromEval(layout, metrics);
        const exceedsShelfDepthGuard =
          layout.maxShelfDepthInches >
          baselineMaxShelfDepth + baselineShelfDepthSlack + EPSILON;
        const improvesLinearFeet =
          optimized.linearFeet + EPSILON < baseline.linearFeet;
        if (exceedsShelfDepthGuard && !improvesLinearFeet) {
          continue;
        }
        if (!passBestOptimized || scoreIsBetter(optimized, passBestOptimized)) {
          passBestOptimized = optimized;
          passBestRotatedById = rotatedNext;
        }
      }
    }

    for (let i = 0; i < eligible.length; i += 1) {
      for (let j = i + 1; j < eligible.length; j += 1) {
        const itemA = eligible[i].item;
        const itemB = eligible[j].item;
        const candidatesA = toRotationCandidates(itemA);
        const candidatesB = toRotationCandidates(itemB);

        for (const candidateRotatedUnitsA of candidatesA) {
          for (const candidateRotatedUnitsB of candidatesB) {
            const rotatedNext = new Map(rotatedById);
            rotatedNext.set(itemA.itemId, candidateRotatedUnitsA);
            rotatedNext.set(itemB.itemId, candidateRotatedUnitsB);

            const { layout, metrics } = evaluateRotatedConfiguration(
              items,
              rotatedNext,
              units,
              customConfig,
              stackingEnabled,
              sideLoadOptions,
              evalCache
            );
            const optimized = metricSliceFromEval(layout, metrics);
            const exceedsShelfDepthGuard =
              layout.maxShelfDepthInches >
              baselineMaxShelfDepth + baselineShelfDepthSlack + EPSILON;
            const improvesLinearFeet =
              optimized.linearFeet + EPSILON < baseline.linearFeet;
            if (exceedsShelfDepthGuard && !improvesLinearFeet) {
              continue;
            }
            if (
              !passBestOptimized ||
              scoreIsBetter(optimized, passBestOptimized)
            ) {
              passBestOptimized = optimized;
              passBestRotatedById = rotatedNext;
            }
          }
        }
      }
    }

    if (!passBestOptimized || !passBestRotatedById) {
      break;
    }
    if (!scoreIsBetter(passBestOptimized, bestOptimized)) {
      break;
    }

    bestOptimized = passBestOptimized;
    bestSuggestions = toSuggestions(passBestRotatedById);
    rotatedById.clear();
    for (const [id, v] of passBestRotatedById) {
      rotatedById.set(id, v);
    }
  }

  const linearFeetGain = baseline.linearFeet - bestOptimized.linearFeet;
  const hasOpportunity =
    linearFeetGain + EPSILON >= SIDELOAD_MIN_LINEAR_FEET_IMPROVEMENT &&
    bestOptimized.linearFeet + EPSILON < baseline.linearFeet;

  const cubicFeetGain = baseline.totalCubicFeet - bestOptimized.totalCubicFeet;
  const hasCubicFeetOnlyOpportunity =
    !hasOpportunity &&
    bestSuggestions.length > 0 &&
    cubicFeetGain + EPSILON >= SIDELOAD_MIN_CUBIC_FEET_IMPROVEMENT &&
    scoreIsBetter(bestOptimized, baseline);

  const surfaceSuggestion = hasOpportunity || hasCubicFeetOnlyOpportunity;

  return {
    hasOpportunity,
    hasCubicFeetOnlyOpportunity,
    current: baseline,
    optimized: surfaceSuggestion ? bestOptimized : baseline,
    suggestions: surfaceSuggestion ? bestSuggestions : [],
  };
}
